import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { AuthService } from '../../../../../../../../../shared/services';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { BidItemSideNavWrapperComponent } from '../bid-item-side-nav-wrapper/bid-item-side-nav-wrapper.component';
import { UploadBidItemsComponent } from '../upload-bid-items/upload-bid-items.component';
import { FetchSettingsBidSheetRequest, SettingsDeleteBidItemRequest } from '../../../../../../../core/projects.actions';
import { projectDetails, settingsBidItemsListInfo, settingsBidSheet } from '../../../../../../../core/projects.selectors';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';

@Component({
  standalone: false,
  selector: 'app-settings-bid-items-list',
  templateUrl: './bid-items-list.component.html',
  styleUrls: ['./bid-items-list.component.scss'],
})
export class SettingsBidItemsListComponent implements OnInit, OnDestroy {
  @ViewChild('sortSelect') sortSelect: any;

  projectId: string;
  bidSheetId: string;

  displayedColumns: string[] = ['item', 'description', 'quantity', 'unit_price', 'uom', 'actions'];
  sortByColumns: string[] = ['Item no', 'Quantity', 'Unit price'];
  selectedSortColumn: string = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  dataSource: any[] = [];
  filteredDataSource: any[] = [];
  originalDataSource: any[] = [];
  selectedRow: any = null;
  totalRecords = 0;
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  currentPage = 1;
  itemsPerPage = 10;
  isOrgAdmin = false;
  downloadingCsv = false;
  /** Project number (or name) used to label the downloaded CSV. */
  projectLabel = '';

  form = new FormGroup({
    searchText: new FormControl(''),
  });

  private readonly onDestroy = new Subject<void>();

  constructor(
    private projectsService: ProjectsService,
    private store: Store,
    private route: ActivatedRoute,
    private authService: AuthService,
    private commonService: CommonService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
      this.fetchBidItemsList();
    }
    const account = this.authService.getCurrentUser();
    const projectRole = account?.roles?.length
      ? account.roles.find((r: any) => r.project_id === this.projectId)?.role ?? account?.role
      : account?.role;
    this.isOrgAdmin = projectRole === 'org-admin';
    this.displayedColumns = this.isOrgAdmin
      ? ['item', 'description', 'quantity', 'unit_price', 'uom', 'actions']
      : ['item', 'description', 'quantity', 'unit_price', 'uom'];

    this.store
      .select(settingsBidSheet)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.id) {
          this.bidSheetId = data.id;
        }
      });

    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.projectLabel = project?.number || project?.name || '';
      });

    this.store
      .select(settingsBidItemsListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;
          if (data?.list?.length > 0) {
            this.originalDataSource = data.list;
            this.filteredDataSource = [...this.originalDataSource];
            this.applyFilters();
          } else {
            this.originalDataSource = [];
            this.filteredDataSource = [];
            this.totalRecords = 0;
            this.dataSource = [];
          }
        }
      });

    this.form.controls.searchText.valueChanges
      .pipe(debounceTime(200), takeUntil(this.onDestroy))
      .subscribe(() => this.applyFilters());

    this.commonService.closeDialog
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dialog.closeAll();
        this.fetchBidItemsList();
      });
  }

  applyFilters(): void {
    let filteredData = [...this.originalDataSource];
    const searchText = (this.form.getRawValue().searchText || '').trim().toLowerCase();
    if (searchText) {
      filteredData = filteredData.filter(
        row =>
          (row?.item && String(row.item).toLowerCase().includes(searchText)) ||
          (row?.description && String(row.description).toLowerCase().includes(searchText))
      );
    }
    filteredData = this.sortItems(filteredData);
    this.filteredDataSource = filteredData;
    this.totalRecords = filteredData.length;
    this.paginateData(true);
  }

  private sortItems(items: any[]): any[] {
    if (!items || !Array.isArray(items)) return [];
    const list = [...items];
    const safeLower = (v: any) => String(v ?? '').toLowerCase();
    const safeNum = (v: any) => (typeof v === 'number' && !Number.isNaN(v) ? v : parseFloat(v) || 0);
    // Natural order so A2 sorts before A10 (plain string compare puts A10, A11… ahead of A2).
    const byItemNo = (a: { item?: unknown }, b: { item?: unknown }) =>
      safeLower(a.item).localeCompare(safeLower(b.item), undefined, { numeric: true, sensitivity: 'base' });
    if (!this.selectedSortColumn) {
      return list.sort(byItemNo);
    }
    const sortFns: Record<string, (a: any, b: any) => number> = {
      'Item no': byItemNo,
      'Quantity': (a, b) => safeNum(a.quantity) - safeNum(b.quantity),
      'Unit price': (a, b) => safeNum(a.unit_price) - safeNum(b.unit_price),
    };
    const fn = sortFns[this.selectedSortColumn];
    if (!fn) return list;
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    return list.sort((a, b) => dir * fn(a, b));
  }

  onSortColumnChange(event: any): void {
    this.selectedSortColumn = event.value;
    this.sortDirection = 'asc';
    this.applyFilters();
  }

  removeSort(event: Event): void {
    event?.stopPropagation();
    this.selectedSortColumn = null;
    this.sortDirection = 'asc';
    this.applyFilters();
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.applyFilters();
  }

  openSortDropdown(event?: Event): void {
    // Material renders the select panel and its backdrop as an inline popover
    // *inside* this wrapper, so the click that closes the panel bubbles back
    // here and would reopen it. Only react to clicks on the button itself.
    const target = event?.target as HTMLElement | null;
    if (target?.closest('.cdk-overlay-popover, .cdk-overlay-backdrop')) return;
    this.sortSelect?.open?.();
  }

  search(): void {
    this.applyFilters();
  }

  closeSearch(): void {
    this.form.controls.searchText.setValue('', { emitEvent: false });
    this.applyFilters();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);
    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  fetchBidItemsList(): void {
    if (!this.projectId) return;
    this.store.dispatch(
      FetchSettingsBidSheetRequest({
        payload: { projectId: this.projectId },
      })
    );
  }

  rowClicked(row: any): void {
    this.selectedRow = row;

    this.projectsService.openRightPanel.next({
      component: BidItemSideNavWrapperComponent,
      data: {
        ...row,
        projectId: this.projectId,
      },
    });
  }

  create(): void {
    this.projectsService.openRightPanel.next({
      component: BidItemSideNavWrapperComponent,
      data: {
        projectId: this.projectId,
      },
    });
  }

  downloadCsv(): void {
    if (!this.bidSheetId || this.downloadingCsv) {
      return;
    }
    this.downloadingCsv = true;
    this.projectsService
      .downloadBidSheetCsv(this.bidSheetId, this.projectLabel)
      .pipe(
        finalize(() => (this.downloadingCsv = false)),
        takeUntil(this.onDestroy),
      )
      .subscribe({
        error: () => this.commonService.notification('Unable to download the bid sheet. Please try again.', 'danger'),
      });
  }

  upload(): void {
    this.dialog.open(UploadBidItemsComponent, {
      disableClose: false,
      data: { projectId: this.projectId, bidSheetId: this.bidSheetId },
      panelClass: 'floating-dialog-box',
    });
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  deleteBidItem(row: any, event: Event): void {
    event.stopPropagation();
    const bidItemLabel = row?.item && row?.description ? `${row.item} - ${row.description}` : 'this bid item';
    const message = `Deleting <b>${bidItemLabel}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.onDestroy)).subscribe(result => {
      if (result) {
        this.store.dispatch(SettingsDeleteBidItemRequest({
          payload: {
            projectId: this.projectId,
            bidItemId: row?.id || row?.uuid,
          },
        }));
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
